import { useState } from 'react';
import { X, Download, Settings, GripVertical } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Card } from '@/components/ui/card/Card';
import { cn } from '@/lib/utils';
import type { Tool, Trace } from '@/features/traceability/type/Tool';
import type { FilterRule, FilterMode, ChartMode, ChartSize } from '../../type/dynamicChart';
import { applyFilters } from '../../utils/filterUtils';
import { exportChartAsPNG } from '../../utils/exportUtils';
import { FilterPanel } from './FilterPanel';
import { ChartConfiguration } from './ChartConfiguration';
import { DynamicChart } from './DynamicChart';
import { ChartErrorBoundary } from './ChartErrorBoundary';
import type { ChartConfig } from '../../type/dynamicChart';

type ChartCardProps = {
  config: ChartConfig;
  traces: Trace[];
  /** Accepted for API parity with SortableChartCard; not currently consumed. */
  tools?: Tool[];
  onUpdate: (updates: Partial<ChartConfig>) => void;
  onRemove: () => void;
  canRemove: boolean;
  mode?: ChartMode;
  isDragging?: boolean;
  dragHandleListeners?: Record<string, unknown>;
  dragHandleAttributes?: Record<string, unknown>;
};

const CHART_SIZES = ['sm', 'md', 'lg', 'full'] as const satisfies readonly ChartSize[];

const glyphWidth: Record<ChartSize, number> = { sm: 3, md: 6, lg: 9, full: 12 };

function SizeGlyph({ size }: { size: ChartSize }) {
  return (
    <svg width="14" height="10" viewBox="0 0 14 10" aria-hidden="true">
      <rect x="1" y="1" width={glyphWidth[size]} height="8" rx="1" fill="currentColor" />
    </svg>
  );
}

export function ChartCard({
  config,
  traces,
  onUpdate,
  onRemove,
  canRemove,
  mode = 'analytics',
  isDragging = false,
  dragHandleListeners,
  dragHandleAttributes,
}: ChartCardProps) {
  const { t } = useTranslation();
  const [showFilters, setShowFilters] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  // Apply filters to traces
  const filteredTraces = applyFilters(traces, config.filters, config.filterMode);

  const handleExport = () => {
    const chartElement = document.getElementById(`chart-${config.id}`);
    if (chartElement) {
      exportChartAsPNG(chartElement, `${config.title}.png`);
    }
  };

  const dragHandleButton = (
    <button
      type="button"
      className={cn(
        'flex h-5 w-5 items-center justify-center rounded text-border transition-colors outline-none hover:bg-muted hover:text-muted-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
        isDragging ? 'cursor-grabbing bg-primary/10 text-primary' : 'cursor-grab'
      )}
      aria-label={t('analytics.dashboard.complexCharts.dragHandle')}
      {...(dragHandleListeners ?? {})}
      {...(dragHandleAttributes ?? {})}
    >
      <GripVertical className="h-3 w-3" aria-hidden />
    </button>
  );

  const sizeSelector = (
    <SegmentedControl<ChartSize>
      size="sm"
      aria-label={t('analytics.dashboard.complexCharts.layoutLabel')}
      value={config.layout.size}
      onChange={(size) => onUpdate({ layout: { ...config.layout, size } })}
      options={CHART_SIZES.map((size) => {
        const sizeLabel = t(`analytics.dashboard.complexCharts.layout.${size}`);
        return {
          value: size,
          label: (
            <span title={sizeLabel} className="flex items-center">
              <SizeGlyph size={size} />
              <span className="sr-only">{sizeLabel}</span>
            </span>
          ),
        };
      })}
    />
  );

  return (
    <Card padding="none">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b p-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {dragHandleButton}
          <input
            type="text"
            value={config.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="w-full min-w-0 rounded border-none bg-transparent px-2 py-1 text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {sizeSelector}

          <Button
            variant="outline"
            size="sm"
            aria-expanded={showFilters}
            onClick={() => setShowFilters(!showFilters)}
          >
            {t('analytics.dashboard.complexCharts.filters.title')}
            {filteredTraces.length < traces.length && (
              <Badge tone="primary" size="sm">
                {filteredTraces.length}
              </Badge>
            )}
          </Button>

          <IconButton
            icon={Settings}
            variant="outline"
            label={t('analytics.dashboard.complexCharts.configuration.title')}
            aria-expanded={showConfig}
            onClick={() => setShowConfig(!showConfig)}
          />

          <IconButton
            icon={Download}
            variant="outline"
            label={t('analytics.dashboard.complexCharts.export.button')}
            onClick={handleExport}
          />

          {canRemove && (
            <IconButton
              icon={X}
              variant="ghost-destructive"
              label={t('analytics.dashboard.complexCharts.removeChart')}
              onClick={onRemove}
            />
          )}
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <FilterPanel
          filters={config.filters}
          filterMode={config.filterMode}
          traces={traces}
          onFiltersChange={(filters: FilterRule[]) => onUpdate({ filters })}
          onFilterModeChange={(filterMode: FilterMode) => onUpdate({ filterMode })}
          mode={mode}
        />
      )}

      {/* Chart Configuration */}
      {showConfig && (
        <ChartConfiguration
          config={config}
          onUpdate={(updates: Partial<ChartConfig>) => onUpdate(updates)}
          mode={mode}
        />
      )}

      {/* Chart */}
      <div id={`chart-${config.id}`} className="p-6">
        <ChartErrorBoundary
          title={t('analytics.dashboard.complexCharts.chartError')}
          description={t('analytics.dashboard.complexCharts.chartErrorDescription')}
          retryLabel={t('analytics.dashboard.complexCharts.chartErrorTryAgain')}
        >
          <DynamicChart config={config} traces={filteredTraces} />
        </ChartErrorBoundary>
      </div>

      {/* Stats Footer */}
      <div className="border-t p-3 text-xs text-muted-foreground">
        {t('analytics.dashboard.complexCharts.showingTraces', {
          filtered: filteredTraces.length,
          total: traces.length,
        })}
      </div>
    </Card>
  );
}
